import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, getApiClient } from '@signalvote/api-client';
import {
  inviteUserRequest,
  updateCampaignRequest,
  updateCapabilitiesRequest,
  type TenantCampaignWithCapabilitiesDto,
  type UpdateCampaignRequest,
  type PendingInvitationDto,
  type UpdateCapabilitiesRequest,
  type SaveScoutAccessRequest,
} from '@signalvote/shared';
import { queryKeys } from '@/lib/queryKeys';
import { useCampaignStore } from '@/stores';

const api = getApiClient();

/**
 * Fetch the current user's campaigns
 */
export function useMyCampaigns() {
  return useQuery(myCampaignsQuery());
}

/** The user's campaigns, synced into the campaign store so the selected campaign carries what the server has now. */
export function myCampaignsQuery() {
  return {
    queryKey: queryKeys.tenantCampaigns.my(),
    queryFn: async () => {
      const campaigns = await getApiClient().tenantCampaigns.getMyCampaigns();
      useCampaignStore.getState().setCampaigns(campaigns);
      return campaigns;
    },
    staleTime: 5 * 60 * 1000,
  };
}

/**
 * Fetch the currently selected campaign details
 */
export function useCurrentCampaign() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.tenantCampaigns.current(),
    queryFn: () => api.tenantCampaigns.getCurrentCampaign(),
    enabled: !!selectedCampaign,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Update an existing tenant campaign
 */
export function useUpdateTenantCampaign() {
  const queryClient = useQueryClient();
  const { selectedCampaign, selectCampaign } = useCampaignStore();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<UpdateCampaignRequest> }) =>
      api.tenantCampaigns.updateCampaign(id, updateCampaignRequest(data)),
    onSuccess: (updatedCampaign, { id, data }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantCampaigns.my() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantCampaigns.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantCampaigns.current() });

      // Sync campaign store so UI reflects changes immediately
      if (selectedCampaign && selectedCampaign.id === id) {
        // Spread all provided data fields (filter out undefined to avoid clearing existing values)
        const definedUpdates = Object.fromEntries(
          Object.entries(data).filter(([, v]) => v !== undefined)
        );
        selectCampaign({
          ...selectedCampaign,
          ...definedUpdates,
        });
      }
    },
  });
}

/**
 * Set/unset a campaign's demo flag (ADR-056). Platform-admin-only — the backend 403s any
 * non-admin (including a campaign owner). On success it invalidates the my-campaigns query,
 * whose refetch re-syncs `selectedCampaign` in the campaign store, so `selectedCampaign.isDemo`
 * and the DEMO banner flip in-session without a manual reload.
 */
export function useSetCampaignDemo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isDemo }: { id: string; isDemo: boolean }) =>
      api.admin.campaigns.setDemo(id, isDemo),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantCampaigns.my() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantCampaigns.current() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantCampaigns.detail(id) });
    },
  });
}

export function useScoutAccess(campaignId: string) {
  return useQuery({
    queryKey: queryKeys.tenantCampaigns.scoutAccess(campaignId),
    queryFn: () => api.admin.campaigns.getScoutAccess(campaignId),
  });
}

export const isScoutAccessConflict = (error: unknown) =>
  error instanceof ApiError && error.status === 409;

/**
 * Save the switch and the whole allowlist. The response carries the fresh token, written into the
 * cache so the next save sends it. A 409 refetches, so nothing stale survives the conflict.
 */
export function useUpdateScoutAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, request }: { campaignId: string; request: SaveScoutAccessRequest }) =>
      api.admin.campaigns.updateScoutAccess(campaignId, request),
    onSuccess: (saved, { campaignId }) => {
      queryClient.setQueryData(queryKeys.tenantCampaigns.scoutAccess(campaignId), saved);
      // The campaign DTO's assistantEnabled is per current user, so the admin's own access may have changed.
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantCampaigns.my() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantCampaigns.current() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantCampaigns.detail(campaignId) });
    },
    onError: (error, { campaignId }) => {
      if (isScoutAccessConflict(error)) {
        queryClient.invalidateQueries({ queryKey: queryKeys.tenantCampaigns.scoutAccess(campaignId) });
      }
    },
  });
}

/**
 * Invite a user to a campaign
 */
export function useInviteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, data }: { campaignId: string; data: Parameters<typeof inviteUserRequest>[0] }) =>
      api.tenantCampaigns.inviteUser(campaignId, inviteUserRequest(data)),
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantCampaigns.users(campaignId),
      });
    },
  });
}

/**
 * Transfer campaign ownership to another member
 */
export function useTransferOwnership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, newOwnerUserId }: { campaignId: string; newOwnerUserId: string }) =>
      api.tenantCampaigns.transferOwnership(campaignId, { newOwnerUserId }),
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantCampaigns.users(campaignId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantCampaigns.all,
      });
    },
  });
}

/**
 * Fetch pending invitations for a campaign
 */
export function usePendingInvitations(campaignId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.tenantCampaigns.invitations(campaignId ?? ''),
    queryFn: () => api.tenantCampaigns.getInvitations(campaignId!),
    enabled: !!campaignId,
  });
}

/**
 * Resend an invitation email
 */
export function useResendInvitation() {
  return useMutation({
    mutationFn: ({ campaignId, invitationId }: { campaignId: string; invitationId: string }) =>
      api.tenantCampaigns.resendInvitation(campaignId, invitationId),
  });
}

/**
 * Cancel a pending invitation
 */
export function useCancelInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, invitationId }: { campaignId: string; invitationId: string }) =>
      api.tenantCampaigns.cancelInvitation(campaignId, invitationId),
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantCampaigns.invitations(campaignId),
      });
    },
  });
}

/**
 * Update a user's capabilities in a campaign
 */
export function useUpdateUserCapabilities() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, userId, data }: { campaignId: string; userId: string; data: Partial<UpdateCapabilitiesRequest> }) =>
      api.tenantCampaigns.updateUserCapabilities(campaignId, userId, updateCapabilitiesRequest(data)),
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantCampaigns.users(campaignId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.volunteers.all,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.vendors.all,
      });
    },
  });
}
